import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import {
  Shield, Users, Coins, Settings, Ban, CheckCircle, AlertTriangle,
  DollarSign, TrendingUp, Activity, Eye, Trash2, Edit, Lock, Unlock,
  MessageSquare, Flag, BarChart3, Database, Server, Key, Zap, Monitor, Bot, Sparkles
} from "lucide-react";
import { useBlockchainEvents } from "@/contexts/BlockchainEventContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { AdminAI } from "@/components/admin/AdminAI";
import { useAdminData } from "@/hooks/useAdminData";
import { Progress } from "@/components/ui/progress";
import { aiService } from "@/lib/ai";
import { dataPipelineClient, CollectorHealthResponse, InfrastructureHealthResponse, CollectionMetricsResponse } from "@/lib/data-pipeline-client";
import { useEffect } from "react";

const platformStats = [
  { label: "Total Revenue", value: "1,234.56 SOL", change: 24.5, icon: DollarSign },
  { label: "Platform Fees", value: "123.4 SOL", change: 12.3, icon: TrendingUp },
  { label: "Active Users", value: "1", change: 8.7, icon: Users },
  { label: "Pending Reports", value: "0", change: 0, icon: Flag },
];

// Reports integration requires backend 'reports' table. Currently empty state.
const mockReports: any[] = [];

const feeSettings = [
  { name: "Trading Fee", value: 1.0, description: "Fee on each trade (buy/sell)" },
  { name: "Token Launch Fee", value: 0.5, description: "SOL fee to launch a new token" },
  { name: "Priority Fee Share", value: 50, description: "% of priority fees kept by platform" },
  { name: "Withdrawal Fee", value: 0.1, description: "Fee on withdrawals" },
  { name: "API Access Fee", value: 100, description: "Monthly fee for API access ($)" },
];

export default function Admin() {
  const { toast } = useToast();
  const { users, coins, loading } = useAdminData();
  const [activeTab, setActiveTab] = useState("overview");
  const [fees, setFees] = useState(feeSettings);
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [tradingEnabled, setTradingEnabled] = useState(true);
  const [launchEnabled, setLaunchEnabled] = useState(true);
  const [apiKey, setApiKey] = useState(aiService.getApiKey());
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [isTraining, setIsTraining] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState<string>("");

  const handleSaveConfig = () => {
    aiService.setApiKey(apiKey);
    toast({ title: "Configuration Saved", description: "AI Agent settings have been updated." });
  };

  const handleStartTraining = async () => {
    setIsTraining(true);
    setTrainingProgress(0);
    setTrainingStatus("Initializing training...");

    // Simulate training progress
    const steps = [
      "Loading training data...",
      "Preprocessing data...",
      "Training model (Epoch 1/5)...",
      "Training model (Epoch 2/5)...",
      "Training model (Epoch 3/5)...",
      "Training model (Epoch 4/5)...",
      "Training model (Epoch 5/5)...",
      "Validating model...",
      "Saving model...",
      "Training complete!"
    ];

    for (let i = 0; i < steps.length; i++) {
      setTrainingStatus(steps[i]);
      setTrainingProgress(((i + 1) / steps.length) * 100);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate time
    }

    setIsTraining(false);
    toast({ title: "Training Complete", description: "AI model has been successfully trained." });
  };

  const handleBanUser = (wallet: string) => {
    toast({ title: "User Banned", description: `Wallet ${wallet} has been banned from the platform.` });
  };

  const handleLockCoin = (ticker: string) => {
    toast({ title: "Token Locked", description: `$${ticker} has been locked by admin.` });
  };

  const handleResolveReport = (id: string) => {
    toast({ title: "Report Resolved", description: "The report has been marked as resolved." });
  };

  return (
    <>
      <Helmet>
        <title>Admin Panel - TheMoonRun</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <div className="flex">
          <Sidebar />

          <main className="flex-1 min-w-0 p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-destructive" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-display font-bold text-foreground">Admin Panel</h1>
                    <p className="text-muted-foreground">Full platform control and management</p>
                  </div>
                </div>
                <Badge variant="destructive" className="text-xs">ADMIN ONLY</Badge>
              </div>

              {/* Platform Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="p-4 rounded-xl bg-card border border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Server className="w-5 h-5 text-primary" />
                    <span className="font-medium">Platform</span>
                  </div>
                  <Switch checked={platformEnabled} onCheckedChange={setPlatformEnabled} />
                </div>
                <div className="p-4 rounded-xl bg-card border border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-success" />
                    <span className="font-medium">Trading</span>
                  </div>
                  <Switch checked={tradingEnabled} onCheckedChange={setTradingEnabled} />
                </div>
                <div className="p-4 rounded-xl bg-card border border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-warning" />
                    <span className="font-medium">Launches</span>
                  </div>
                  <Switch checked={launchEnabled} onCheckedChange={setLaunchEnabled} />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {platformStats.map((stat) => (
                  <div key={stat.label} className="p-5 rounded-xl bg-card border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <stat.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className={cn(
                        "text-sm font-medium",
                        stat.change >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {stat.change >= 0 ? "+" : ""}{stat.change}%
                      </span>
                    </div>
                    <p className="text-2xl font-mono font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="ai-brain" className="text-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20">
                    <Bot className="w-4 h-4 mr-2" />
                    AI Brain
                  </TabsTrigger>
                  <TabsTrigger value="users">Users</TabsTrigger>
                  <TabsTrigger value="users">Users</TabsTrigger>
                  <TabsTrigger value="tokens">Tokens</TabsTrigger>
                  <TabsTrigger value="reports">Reports</TabsTrigger>
                  <TabsTrigger value="fees">Fees & Revenue</TabsTrigger>
                  <TabsTrigger value="api">API Keys</TabsTrigger>
                  <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="p-6 rounded-xl bg-card border border-border">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        Revenue Breakdown
                      </h3>
                      <div className="space-y-4">
                        {[
                          { label: "Trading Fees", value: "$12,450", percent: 68 },
                          { label: "Launch Fees", value: "$3,200", percent: 18 },
                          { label: "Priority Fees", value: "$1,890", percent: 10 },
                          { label: "API Access", value: "$690", percent: 4 },
                        ].map((item) => (
                          <div key={item.label}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-muted-foreground">{item.label}</span>
                              <span className="font-mono text-foreground">{item.value}</span>
                            </div>
                            <div className="h-2 rounded-full bg-secondary">
                              <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${item.percent}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-6 rounded-xl bg-card border border-border">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-warning" />
                        Recent Alerts
                      </h3>
                      <div className="space-y-3">
                        {[
                          { msg: "High volume spike detected on $MDOGE", time: "5m ago", type: "info" },
                          { msg: "New scam report filed for $SCAM", time: "1h ago", type: "warning" },
                          { msg: "User moon_hunter suspended", time: "3h ago", type: "error" },
                          { msg: "API rate limit exceeded by client_xyz", time: "6h ago", type: "warning" },
                        ].map((alert, i) => (
                          <div key={i} className={cn(
                            "p-3 rounded-lg border",
                            alert.type === "info" && "bg-primary/5 border-primary/20",
                            alert.type === "warning" && "bg-warning/5 border-warning/20",
                            alert.type === "error" && "bg-destructive/5 border-destructive/20"
                          )}>
                            <p className="text-sm text-foreground">{alert.msg}</p>
                            <p className="text-xs text-muted-foreground mt-1">{alert.time}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="ai-brain" className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <AdminAI />
                    </div>
                    <div className="space-y-6">
                      <div className="p-6 rounded-xl bg-card border border-border">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-primary" />
                          Model Configuration
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium mb-1 block">Provider</label>
                            <select className="w-full bg-secondary border border-border rounded-md p-2 text-sm">
                              <option>OpenRouter (Recommended)</option>
                              <option>OpenAI</option>
                              <option>Anthropic</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">Model</label>
                            <select className="w-full bg-secondary border border-border rounded-md p-2 text-sm">
                              <option>Anthropic: Claude 3.5 Sonnet</option>
                              <option>OpenAI: GPT-4o</option>
                              <option>Meta: Llama 3 70B</option>
                            </select>
                          </div>
                          <div className="flex items-center justify-between pt-2">
                            <span className="text-sm text-muted-foreground">Auto-Execution</span>
                            <Switch defaultChecked />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-1 block">OpenRouter API Key</label>
                            <Input
                              type="password"
                              value={apiKey}
                              onChange={(e) => setApiKey(e.target.value)}
                              placeholder="sk-or-..."
                              className="bg-secondary mb-2"
                            />
                            <Button size="sm" onClick={handleSaveConfig} className="w-full">Save Configuration</Button>
                          </div>
                        </div>

                      </div>

                      <div className="p-6 rounded-xl bg-card border border-border">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Zap className="w-5 h-5 text-primary" />
                          Model Training
                        </h3>
                        <div className="space-y-4">
                          {isTraining && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">Training Progress</span>
                                <span className="text-sm font-mono text-foreground">{Math.round(trainingProgress)}%</span>
                              </div>
                              <Progress value={trainingProgress} className="w-full mb-2" />
                              <p className="text-sm text-center text-foreground">{trainingStatus}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4">
                            <Button
                              onClick={handleStartTraining}
                              disabled={isTraining}
                              className="w-full"
                            >
                              {isTraining ? "Training..." : "Start Training"}
                            </Button>
                            <Button variant="outline" className="w-full">
                              View Training History
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <p>Last trained: 2 hours ago</p>
                            <p>Training data: 10,000+ trading patterns</p>
                            <p>Model accuracy: 94.2%</p>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 rounded-xl bg-card border border-border">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-primary" />
                          Agent Activity
                        </h3>
                        <div className="space-y-4">
                          {[
                            { action: "Adjusted fee", time: "2m ago", status: "success" },
                            { action: "Flagged user", time: "15m ago", status: "success" },
                            { action: "Scan logs", time: "1h ago", status: "success" },
                          ].map((act, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{act.action}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs opacity-50">{act.time}</span>
                                <div className="h-2 w-2 rounded-full bg-success" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="users">
                  <div className="rounded-xl bg-card border border-border overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold">User Management</h3>
                      <Input placeholder="Search users..." className="w-64" />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Wallet</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Username</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Trades</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Volume</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {users.map((user) => (
                            <tr key={user.wallet_address} className="hover:bg-secondary/30">
                              <td className="px-4 py-3 font-mono text-sm">{user.wallet_address.slice(0, 8)}...</td>
                              <td className="px-4 py-3 text-sm">{user.username || "Anon"}</td>
                              <td className="px-4 py-3 text-sm font-mono">0</td>
                              <td className="px-4 py-3 text-sm font-mono">$0</td>
                              <td className="px-4 py-3">
                                <Badge variant="default">
                                  active
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-destructive"
                                    onClick={() => handleBanUser(user.wallet_address)}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {users.length === 0 && (
                            <tr><td colSpan={6} className="text-center p-4 text-muted-foreground">No users found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tokens">
                  <div className="rounded-xl bg-card border border-border overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold">Token Management</h3>
                      <Input placeholder="Search tokens..." className="w-64" />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Token</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Creator</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Volume</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Reports</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {coins.map((coin) => (
                            <tr key={coin.id} className="hover:bg-secondary/30">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-sm">{coin.name}</p>
                                  <p className="text-xs text-muted-foreground">${coin.ticker}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 font-mono text-sm">{coin.creator_wallet.slice(0, 8)}...</td>
                              <td className="px-4 py-3 text-sm font-mono">${(coin.volume_24h || 0).toLocaleString()}</td>
                              <td className="px-4 py-3">
                                <Badge variant="secondary">
                                  0
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant={
                                  coin.status === "active" ? "default" : "secondary"
                                }>
                                  {coin.status || "active"}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleLockCoin(coin.ticker)}
                                  >
                                    {coin.status === "locked" ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {coins.length === 0 && (
                            <tr><td colSpan={6} className="text-center p-4 text-muted-foreground">No tokens found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="reports">
                  <div className="space-y-4">
                    {mockReports.length > 0 ? (
                      mockReports.map((report) => (
                        <div key={report.id} className="p-4 rounded-xl bg-card border border-border">
                          {/* Report Item Content */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={cn(
                                "w-10 h-10 rounded-lg flex items-center justify-center",
                                report.type === "scam" && "bg-destructive/10",
                                report.type === "harassment" && "bg-warning/10",
                                report.type === "spam" && "bg-muted"
                              )}>
                                <Flag className={cn(
                                  "w-5 h-5",
                                  report.type === "scam" && "text-destructive",
                                  report.type === "harassment" && "text-warning",
                                  report.type === "spam" && "text-muted-foreground"
                                )} />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">
                                  {report.type.charAt(0).toUpperCase() + report.type.slice(1)} Report
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Target: <span className="text-foreground">{report.target}</span> •
                                  Reporter: <span className="font-mono">{report.reporter}</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">{report.time}</span>
                              <Badge variant={report.status === "pending" ? "destructive" : "secondary"}>
                                {report.status}
                              </Badge>
                              {report.status === "pending" && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResolveReport(report.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Resolve
                                  </Button>
                                  <Button size="sm" variant="destructive">
                                    <Ban className="w-4 h-4 mr-1" />
                                    Take Action
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-xl">
                        <Shield className="w-12 h-12 mx-auto mb-4 opacity-20" />
                        <p>No active reports</p>
                        <p className="text-xs mt-1">Great job! The platform is clean.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="fees">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="p-6 rounded-xl bg-card border border-border">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-primary" />
                        Platform Fees
                      </h3>
                      <div className="space-y-4">
                        {fees.map((fee, i) => (
                          <div key={fee.name} className="space-y-2">
                            <div className="flex justify-between">
                              <label className="text-sm font-medium">{fee.name}</label>
                              <span className="text-xs text-muted-foreground">{fee.description}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={fee.value}
                                onChange={(e) => {
                                  const newFees = [...fees];
                                  newFees[i].value = parseFloat(e.target.value) || 0;
                                  setFees(newFees);
                                }}
                                className="w-24"
                              />
                              <span className="text-sm text-muted-foreground">
                                {fee.name.includes("$") || fee.name.includes("API") ? "$" : "%"}
                              </span>
                            </div>
                          </div>
                        ))}
                        <Button className="w-full mt-4">
                          Update Fees
                        </Button>
                      </div>
                    </div>

                    <div className="p-6 rounded-xl bg-card border border-border">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Database className="w-5 h-5 text-primary" />
                        Treasury
                      </h3>
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-secondary/50">
                          <p className="text-sm text-muted-foreground">Platform Treasury</p>
                          <p className="text-3xl font-mono font-bold text-foreground">1,234.56 SOL</p>
                          <p className="text-sm text-success">≈ $127,450 USD</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <Button variant="outline" className="w-full">
                            View History
                          </Button>
                          <Button className="w-full">
                            Withdraw
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="api">
                  <div className="p-6 rounded-xl bg-card border border-border">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Key className="w-5 h-5 text-primary" />
                      API Keys Management
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Manage API keys for third-party integrations. Each key costs $100/month.
                    </p>
                    <div className="space-y-4">
                      {[
                        { name: "TradingBot_Pro", key: "tmr_live_****...****7x9k", status: "active", calls: 125000 },
                        { name: "Analytics_Dashboard", key: "tmr_live_****...****3m2p", status: "active", calls: 45000 },
                        { name: "Mobile_App", key: "tmr_test_****...****9h1j", status: "test", calls: 8900 },
                      ].map((api) => (
                        <div key={api.name} className="p-4 rounded-lg bg-secondary/50 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{api.name}</p>
                            <p className="font-mono text-xs text-muted-foreground">{api.key}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-mono">{api.calls.toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">API calls</p>
                            </div>
                            <Badge variant={api.status === "active" ? "default" : "secondary"}>
                              {api.status}
                            </Badge>
                            <Button size="sm" variant="ghost" className="text-destructive">
                              Revoke
                            </Button>
                          </div>
                        </div>
                      ))}
                      <Button className="w-full mt-4">
                        Generate New API Key
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="monitoring">
                  <MonitoringTab />
                </TabsContent>
              </Tabs>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

function MonitoringTab() {
  const { eventListener, isConnected } = useBlockchainEvents();
  const [collectorHealth, setCollectorHealth] = useState<CollectorHealthResponse | null>(null);
  const [infrastructureHealth, setInfrastructureHealth] = useState<InfrastructureHealthResponse | null>(null);
  const [collectionMetrics, setCollectionMetrics] = useState<CollectionMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const monitor = eventListener ? eventListener.getMonitor() : null;
  const stats = monitor ? monitor.getStats() : { eventCounts: {}, lastEventTimes: {} };

  useEffect(() => {
    const fetchDataPipelineHealth = async () => {
      try {
        setLoading(true);
        setError(null);

        const [collectors, infrastructure, metrics] = await Promise.all([
          dataPipelineClient.getCollectorHealth(),
          dataPipelineClient.getInfrastructureHealth(),
          dataPipelineClient.getCollectionMetrics()
        ]);

        setCollectorHealth(collectors);
        setInfrastructureHealth(infrastructure);
        setCollectionMetrics(metrics);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data pipeline health');
        console.error('Data pipeline health fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDataPipelineHealth();

    // Refresh every 30 seconds
    const interval = setInterval(fetchDataPipelineHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTriggerCollection = async () => {
    try {
      // This would need a backend endpoint to trigger collection
      // For now, just show a toast
      toast({ title: "Collection Triggered", description: "Manual collection cycle has been initiated." });
    } catch (err) {
      toast({ title: "Error", description: "Failed to trigger collection cycle.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Data Pipeline Monitoring */}
      <div className="p-6 rounded-xl bg-card border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Data Pipeline Monitoring
          </h3>
          <Button onClick={handleTriggerCollection} size="sm">
            <Zap className="w-4 h-4 mr-2" />
            Trigger Collection
          </Button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <p className="text-muted-foreground">Loading data pipeline health...</p>
          </div>
        )}

        {error && (
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Collector Health */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Collector Health
              </h4>
              <div className="space-y-2">
                {collectorHealth && Object.entries(collectorHealth).map(([collector, health]) => (
                  <div key={collector} className="flex items-center justify-between p-2 rounded bg-secondary/50">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        health.status === "healthy" ? "bg-success" : "bg-destructive"
                      )} />
                      <span className="text-sm capitalize">{collector.replace('_', ' ')}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">
                        {health.last_run ? new Date(health.last_run).toLocaleTimeString() : 'Never'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {Math.round(health.success_rate * 100)}% success
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Infrastructure Health */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Server className="w-4 h-4" />
                Infrastructure Health
              </h4>
              <div className="space-y-2">
                {infrastructureHealth && Object.entries(infrastructureHealth).map(([service, health]) => (
                  <div key={service} className="flex items-center justify-between p-2 rounded bg-secondary/50">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        health.status === "connected" || health.status === "running" ? "bg-success" : "bg-destructive"
                      )} />
                      <span className="text-sm capitalize">{service}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{health.details}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Collection Metrics */}
        {!loading && !error && collectionMetrics && (
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Real-time Collection Metrics
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-primary">{collectionMetrics.solana_records_per_minute}</p>
                <p className="text-xs text-muted-foreground">Solana rec/min</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-primary">{collectionMetrics.token_records_per_minute}</p>
                <p className="text-xs text-muted-foreground">Token rec/min</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-primary">{collectionMetrics.dex_records_per_minute}</p>
                <p className="text-xs text-muted-foreground">DEX rec/min</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-mono font-bold text-primary">{Math.round(collectionMetrics.error_rate * 100)}%</p>
                <p className="text-xs text-muted-foreground">Error rate</p>
              </div>
            </div>

            {/* Alerts */}
            {collectionMetrics.error_rate > 0.1 && (
              <div className="mt-4 p-3 rounded-lg bg-warning/10 border border-warning/20">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  <span className="text-sm font-medium text-warning">High error rate detected</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Collection error rate is {Math.round(collectionMetrics.error_rate * 100)}%. Check collector health.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Blockchain Event Monitoring */}
      <div className="p-6 rounded-xl bg-card border border-border">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Monitor className="w-5 h-5 text-primary" />
          Blockchain Event Monitoring
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3">Connection Status</h4>
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-3 h-3 rounded-full",
                isConnected ? "bg-success" : "bg-destructive"
              )} />
              <span className="text-sm">{isConnected ? "Connected" : "Disconnected"}</span>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-3">Event Counts</h4>
            <div className="space-y-2">
              {Object.entries(stats.eventCounts).map(([eventType, count]) => (
                <div key={eventType} className="flex justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{eventType.replace('_', ' ')}</span>
                  <span className="font-mono">{count}</span>
                </div>
              ))}
              {Object.keys(stats.eventCounts).length === 0 && (
                <p className="text-sm text-muted-foreground">No events recorded yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-card border border-border">
        <h4 className="font-medium mb-3">Last Event Times</h4>
        <div className="space-y-2">
          {Object.entries(stats.lastEventTimes).map(([eventType, timestamp]) => (
            <div key={eventType} className="flex justify-between text-sm">
              <span className="text-muted-foreground capitalize">{eventType.replace('_', ' ')}</span>
              <span className="font-mono">{new Date(timestamp).toLocaleString()}</span>
            </div>
          ))}
          {Object.keys(stats.lastEventTimes).length === 0 && (
            <p className="text-sm text-muted-foreground">No events recorded yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
